import { startOMSFormTiming } from '@src/components/OMS/NewOrder/OMSFormTiming';
import { orderSliceActions } from '@src/components/OMS/NewOrder/OrderSlice';
import type { PrimeOMSParams } from '@src/components/OMS/NewOrder/types';
import { openView } from '@src/components/OMS/OMSSlice';
import { OMSView } from '@src/components/OMS/OMSView';
import { useGetOrderCurrency } from '@src/components/OMS/useGetOrderCurrency';
import { useFeatureFlag } from '@src/hooks';
import { useDefaultSubAccount } from '@src/hooks/useDefaultSubAccount';
import { useTradingSettings } from '@src/providers/AppConfigProvider';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import { useOrderPresets } from '@src/providers/OrderPresetsContext';
import {
  MixpanelEvent,
  SideEnum,
  useMixpanel,
  useSecuritiesContext,
  type Allocation,
  type ExpressedOutcomeEnum,
} from '@talos/kyoko';
import { useCallback } from 'react';

export function usePrimePredictionOrder() {
  const dispatch = useAppStateDispatch();
  const mixpanel = useMixpanel();
  const { enablePredictionMarkets } = useFeatureFlag();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { getOrderCurrency } = useGetOrderCurrency();
  const { defaultSubAccount } = useDefaultSubAccount();
  const { enableDefaultMarketCardSubAccount } = useTradingSettings();
  const { orderPresetsByID } = useOrderPresets();
  const isOrderFormOpen = useAppStateSelector(state => state.OMS.openedView === OMSView.NewOrderForm);

  return useCallback(
    ({
      symbol,
      outcome,
      marketAccounts,
      aggregation,
      orderPresetID,
      orderQty,
      subAccount,
      price,
    }: {
      symbol: string;
      outcome: ExpressedOutcomeEnum;
      marketAccounts?: string[];
      aggregation?: string;
      orderPresetID?: string;
      orderQty?: string;
      subAccount?: string;
      price?: string;
    }) => {
      if (!enablePredictionMarkets) {
        return;
      }

      const security = securitiesBySymbol.get(symbol);
      if (!security) {
        return;
      }

      const side = SideEnum.Buy;

      mixpanel.track(MixpanelEvent.PrimeOrder);

      const cardSubAccount = enableDefaultMarketCardSubAccount ? subAccount : undefined;
      const orderPreset = orderPresetID ? orderPresetsByID.get(orderPresetID) : undefined;
      // A sub account set on the card overrides both the order preset allocations and the tab default.
      const allocations = cardSubAccount
        ? [{ subAccount: cardSubAccount, value: '1' } satisfies Allocation]
        : (orderPreset?.allocations ??
          (defaultSubAccount?.Name
            ? [{ subAccount: defaultSubAccount.Name, value: '1' } satisfies Allocation]
            : undefined));

      const order: PrimeOMSParams = {
        symbol,
        side,
        price,
        currency: getOrderCurrency(security, side),
        primeFrom: 'MarketCard',
        predictionOutcome: outcome,
        orderQty,
        aggregation,
        orderPreset: orderPreset ? { ...orderPreset, allocations: allocations ?? [] } : undefined,
        subAccountAllocations: allocations,
        ...(marketAccounts?.length ? { marketAccounts } : {}),
      };

      if (!isOrderFormOpen) {
        startOMSFormTiming(OMSView.NewOrderForm);
      }
      dispatch(openView(OMSView.NewOrderForm));
      dispatch(orderSliceActions.primeNewOrderForm(order));
    },
    [
      defaultSubAccount?.Name,
      dispatch,
      enableDefaultMarketCardSubAccount,
      enablePredictionMarkets,
      getOrderCurrency,
      isOrderFormOpen,
      mixpanel,
      orderPresetsByID,
      securitiesBySymbol,
    ]
  );
}
